import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const TITLE = seoTitle("Sobre a Gazeta Regional Online — Webjornal Independente");
const DESC = seoDescription(
  "Conheça a Gazeta Regional Online: webjornal independente com notícias, reviews de produtos, guias práticos e tutoriais para o leitor brasileiro.",
);

export const Route = createFileRoute("/sobre")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "/sobre" },
    ],
    links: [{ rel: "canonical", href: `${SITE.baseUrl}/sobre` }],
  }),
  component: Sobre,
});

const values = [
  { t: "Apuração honesta", d: "Buscamos fontes, checamos dados e citamos referências. Sem sensacionalismo, sem manchete enganosa." },
  { t: "Serviço ao leitor", d: "Escrevemos para ajudar quem lê a entender um tema, resolver um problema ou decidir uma compra." },
  { t: "Reviews independentes", d: "Nossos testes têm prós e contras reais — nunca páginas exclusivamente promocionais de marca." },
  { t: "Leitura leve", d: "Texto claro, direto e sem enrolação. Priorizamos a experiência de leitura em qualquer dispositivo." },
];

const method = [
  "A pauta diária é discutida coletivamente pela redação",
  "Cada matéria tem um repórter responsável e passa por edição",
  "Notícias são apuradas em fontes públicas, oficiais e primárias",
  "Reviews envolvem pesquisa, comparação e teste antes da recomendação",
  "Guias e tutoriais são testados antes de virar passo a passo",
  "Correções e atualizações são registradas ao final de cada matéria",
];

function Sobre() {
  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Capa", to: "/" }, { label: "Sobre" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Sobre o jornal</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
              <span className="text-gradient">{SITE.name}</span> — jornalismo de serviço em formato digital.
            </h1>
            <p className="mt-4 text-muted-foreground md:text-lg">
              Somos um webjornal independente brasileiro. Cobrimos notícias e atualidades e produzimos reviews, guias práticos, tutoriais passo a passo, faça-você-mesmo e artigos tira-dúvidas para o dia a dia do leitor.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/blog" className="btn-accent">Ler as últimas notícias</Link>
              <Link to="/contato" className="btn-ghost">Falar com a redação</Link>
            </div>
          </div>
        </div>
      </section>

      <Section>
        <SectionHeader eyebrow="Nossos valores" title="Princípios da redação" />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <Reveal key={v.t} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <div className="card-glass">
                <h3 className="text-lg font-semibold">{v.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-[color:var(--color-sidebar)]">
        <Reveal>
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <span className="eyebrow">Metodologia</span>
              <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">Como fazemos cada matéria</h2>
              <p className="mt-4 text-muted-foreground">
                Todo conteúdo publicado na Gazeta Regional Online passa por um fluxo editorial claro: apuração, redação, edição e checagem. A meta é entregar informação confiável, útil e de leitura leve.
              </p>
            </div>
            <ul className="grid gap-3">
              {method.map((m) => (
                <li key={m} className="flex items-start gap-3 rounded border border-border bg-card p-4">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-accent text-primary">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
                      <path d="m5 12 5 5L20 7" />
                    </svg>
                  </span>
                  <span className="text-sm">{m}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </Section>

      <Section>
        <Reveal>
          <div className="rounded border-2 border-primary bg-card p-8 md:p-10">
            <h2 className="text-2xl font-bold">Transparência editorial</h2>
            <p className="mt-4 text-sm text-muted-foreground">
              Reviews e comparativos podem conter links de parceiros — quando existirem, isso é informado no próprio artigo. O critério de recomendação é sempre editorial: indicamos o que consideramos melhor para o leitor.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Encontrou um erro ou tem uma sugestão de pauta? Escreva para <a href={`mailto:${SITE.email}`} className="text-primary underline font-semibold">{SITE.email}</a>.
            </p>
          </div>
        </Reveal>
      </Section>

      <CTASection title="Acompanhe as últimas edições" text="Notícias, reviews e guias atualizados diariamente pela redação da Gazeta Regional Online." buttonLabel="Ir para as editorias" />
    </>
  );
}
